import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useSalesPersonSession, useTracker } from '../../context/TrackerContext';
import type { SalesGoalData, SessionSlot, SpPayment, SpWeekData } from '../../lib/api';
import { getMyEodToday, getPublicSessionSlots, getSalesPersonDashboard, getSpMotivation, submitEOD, SLOT_COURSES } from '../../lib/api';
import type { EodCounts } from '../../lib/api';
import LeavePanel from '../../components/LeavePanel';
import { homePage, saveCrmSession } from '../../crm/session';
import type { CrmSession } from '../../crm/session';
import { forgetCrmMe } from '../../crm/ui';

/* ─── Tier config ──────────────────────────────────────────────────────────── */
type TierName = 'Bronze' | 'Silver' | 'Gold' | 'Diamond' | 'Diplomat' | 'None';
const TIER: Record<TierName, { grad: string; light: string; accent: string; badge: string; emoji: string }> = {
  None:     { grad:'linear-gradient(135deg,#6b7280,#9ca3af)', light:'#f3f4f6', accent:'#6b7280', badge:'#6b7280', emoji:'⭐' },
  Bronze:   { grad:'linear-gradient(135deg,#b45309,#f59e0b)', light:'#fffbeb', accent:'#d97706', badge:'#d97706', emoji:'🥉' },
  Silver:   { grad:'linear-gradient(135deg,#475569,#94a3b8)', light:'#f1f5f9', accent:'#64748b', badge:'#64748b', emoji:'🥈' },
  Gold:     { grad:'linear-gradient(135deg,#92400e,#fbbf24)', light:'#fef3c7', accent:'#f59e0b', badge:'#f59e0b', emoji:'🥇' },
  Diamond:  { grad:'linear-gradient(135deg,#0369a1,#38bdf8)', light:'#e0f2fe', accent:'#0ea5e9', badge:'#0ea5e9', emoji:'💎' },
  Diplomat: { grad:'linear-gradient(135deg,#5b21b6,#a78bfa)', light:'#f5f3ff', accent:'#7c3aed', badge:'#7c3aed', emoji:'👑' },
};
const ALL_TIERS: { name: TierName; sales: number; pct: number }[] = [
  { name:'Bronze', sales:5, pct:5 }, { name:'Silver', sales:7, pct:7 },
  { name:'Gold', sales:10, pct:10 }, { name:'Diamond', sales:15, pct:15 },
  { name:'Diplomat', sales:18, pct:20 },
];
function getTierInfo(n: string) { return TIER[(n as TierName) in TIER ? (n as TierName) : 'None']; }
function fmtINR(n: number) { return '₹' + n.toLocaleString('en-IN'); }

/* ─── Incentive status colours ─────────────────────────────────────────────── */
// Given by admin  → vibrant green
// Earned, not given → amber/orange
// Partial (waiting) → blue
// Ran Away → red
const INC = {
  given:    { bg:'#dcfce7', text:'#15803d', border:'#86efac', glow:'rgba(34,197,94,.25)',  label:'Incentive Given',   icon:'✅' },
  notGiven: { bg:'#fef9c3', text:'#92400e', border:'#fcd34d', glow:'rgba(251,191,36,.25)', label:'Earned – Awaiting', icon:'💰' },
  partial:  { bg:'#e0f2fe', text:'#075985', border:'#7dd3fc', glow:'rgba(14,165,233,.2)',  label:'Partial Payment',   icon:'⏳' },
  runaway:  { bg:'#fee2e2', text:'#991b1b', border:'#fca5a5', glow:'rgba(239,68,68,.2)',   label:'Ran Away',          icon:'❌' },
};
function incStatus(p: SpPayment): keyof typeof INC {
  if (p.isRunaway)  return 'runaway';
  if (p.isPartial)  return 'partial';
  if (p.incentivePaid) return 'given';
  return 'notGiven';
}

/* ─── CSS ──────────────────────────────────────────────────────────────────── */
const CSS = `
  @keyframes fadeUp    { from{opacity:0;transform:translateY(16px)} to{opacity:1;transform:none} }
  @keyframes slideUp   { from{transform:translateY(100%)}            to{transform:translateY(0)} }
  @keyframes slideIn   { from{transform:translateX(-100%)}           to{transform:translateX(0)} }
  @keyframes shine     { from{transform:translateX(-120%) rotate(25deg)} to{transform:translateX(260%) rotate(25deg)} }
  @keyframes pulse     { 0%,100%{box-shadow:0 0 0 0 var(--glow)} 60%{box-shadow:0 0 0 6px transparent} }
  @keyframes floatOrb  { 0%,100%{transform:translateY(0) scale(1)} 50%{transform:translateY(-18px) scale(1.06)} }
  @keyframes floatOrb2 { 0%,100%{transform:translateY(0) scale(1)} 50%{transform:translateY(14px) scale(.94)} }
  @keyframes typeText  { from{clip-path:inset(0 100% 0 0)} to{clip-path:inset(0 0% 0 0)} }
  @keyframes progressFill { from{width:0} to{width:var(--target-w)} }
  @keyframes sparkle   { 0%,100%{opacity:0;transform:scale(0) rotate(0)} 50%{opacity:1;transform:scale(1) rotate(180deg)} }
  @keyframes confetti  { 0%{transform:translateY(0) rotate(0);opacity:1} 100%{transform:translateY(80px) rotate(720deg);opacity:0} }
  @keyframes gradShift  { 0%{background-position:0% 50%} 50%{background-position:100% 50%} 100%{background-position:0% 50%} }
  @keyframes motivePulse{ 0%,100%{box-shadow:0 0 0 0 rgba(255,255,255,.18),0 4px 20px rgba(0,0,0,.2)} 50%{box-shadow:0 0 0 6px rgba(255,255,255,.08),0 8px 32px rgba(0,0,0,.25)} }

  .fu1{animation:fadeUp .35s ease both}
  .fu2{animation:fadeUp .35s .07s ease both}
  .fu3{animation:fadeUp .35s .13s ease both}
  .fu4{animation:fadeUp .35s .19s ease both}
  .fu5{animation:fadeUp .35s .25s ease both}

  .kpi{transition:transform .15s ease,box-shadow .15s ease;overflow:hidden;position:relative}
  .kpi:hover{transform:translateY(-2px)}
  .kpi::after{content:'';position:absolute;inset:0;background:linear-gradient(120deg,transparent 25%,rgba(255,255,255,.2) 50%,transparent 65%);transform:translateX(-120%) rotate(25deg);pointer-events:none}
  .kpi:hover::after{animation:shine .6s ease}

  .status-chip{display:inline-flex;align-items:center;gap:3px;font-weight:700;border-radius:99px;white-space:nowrap;border-width:1px;border-style:solid}
  .given-pulse{animation:pulse 2.5s infinite;--glow:rgba(34,197,94,.35)}
  .notGiven-pulse{animation:pulse 2.5s infinite;--glow:rgba(251,191,36,.35)}

  .pay-row{transition:background .12s ease}
  .pay-row:hover{background:#f8faff}
  .week-row{transition:all .15s ease;cursor:pointer}
  .week-row:hover{background:#f0f4ff;transform:translateX(2px)}

  .nav-item{display:flex;align-items:center;gap:10px;padding:10px 14px;border-radius:12px;cursor:pointer;transition:all .15s ease;border:none;background:transparent;width:100%;text-align:left;font-size:13px;font-weight:600;color:#64748b;font-family:inherit}
  .nav-item:hover{background:#f0f4ff;color:#4c1d95}
  .nav-item.active{background:linear-gradient(135deg,#ede9fe,#ddd6fe);color:#5b21b6;font-weight:800}

  .sidebar-overlay{display:none}
  @media(max-width:767px){
    .sidebar{transform:translateX(-100%);transition:transform .25s ease}
    .sidebar.open{transform:translateX(0);animation:slideIn .25s ease}
    .sidebar-overlay{display:block}
    .main-wrap{margin-left:0!important}
  }
  @media(min-width:768px){
    .sidebar{transform:none!important}
    .hamburger{display:none!important}
  }
`;

/* ─── Micro components ─────────────────────────────────────────────────────── */
const Counter: React.FC<{ to: number; prefix?: string }> = ({ to, prefix = '' }) => {
  const [v, setV] = useState(0);
  const raf = useRef<number>(0);
  useEffect(() => {
    const t0 = Date.now(), dur = 900;
    const tick = () => {
      const p = Math.min(1, (Date.now()-t0)/dur);
      setV(Math.round(to * (1 - Math.pow(1-p, 4))));
      if (p < 1) raf.current = requestAnimationFrame(tick);
    };
    raf.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf.current);
  }, [to]);
  return <>{prefix}{v.toLocaleString('en-IN')}</>;
};

/* Inline SVG sparkline */
const Sparkline: React.FC<{ vals: number[]; color: string; w?: number; h?: number }> = ({
  vals, color, w = 80, h = 32,
}) => {
  if (vals.length < 2) return <div style={{ width:w, height:h }} />;
  const max = Math.max(...vals, 1);
  const pts = vals.map((val, i) => {
    const x = ((i/(vals.length-1))*w).toFixed(1);
    const y = (h - 3 - (val/max)*(h-6)).toFixed(1);
    return `${x},${y}`;
  });
  const id = `sp${color.replace(/\W/g,'')}`;
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} style={{ overflow:'visible' }}>
      <defs>
        <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"   stopColor={color} stopOpacity=".4" />
          <stop offset="100%" stopColor={color} stopOpacity="0"  />
        </linearGradient>
      </defs>
      <polygon points={`0,${h} ${pts.join(' ')} ${w},${h}`} fill={`url(#${id})`} />
      <polyline points={pts.join(' ')} fill="none" stroke={color} strokeWidth="2"
        strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={pts[pts.length-1].split(',')[0]} cy={pts[pts.length-1].split(',')[1]}
        r="3" fill={color} />
    </svg>
  );
};

/* SVG progress ring */
const Ring: React.FC<{ pct: number; size?: number; thick?: number; color: string; label?: string; sub?: string }> = ({
  pct, size = 88, thick = 9, color, label, sub,
}) => {
  const r = (size-thick)/2, circ = 2*Math.PI*r, dash = Math.min(pct/100,1)*circ;
  const cx = size/2;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <circle cx={cx} cy={cx} r={r} fill="none" stroke="#f1f5f9" strokeWidth={thick} />
      <circle cx={cx} cy={cx} r={r} fill="none" stroke={color} strokeWidth={thick}
        strokeLinecap="round" strokeDasharray={`${dash} ${circ}`}
        transform={`rotate(-90 ${cx} ${cx})`}
        style={{ transition:'stroke-dasharray 1s ease' }} />
      {label && <text x={cx} y={cx-(sub?7:0)} textAnchor="middle" fontSize={size*.18} fontWeight="900" fill="#0f172a" fontFamily="Plus Jakarta Sans,Inter,sans-serif">{label}</text>}
      {sub   && <text x={cx} y={cx+size*.14} textAnchor="middle" fontSize={size*.1}  fontWeight="700" fill="#94a3b8" fontFamily="Plus Jakarta Sans,Inter,sans-serif">{sub}</text>}
    </svg>
  );
};

/* Donut */
const Donut: React.FC<{ segs: {val:number;color:string}[]; size?: number; thick?: number; center?: string; csub?: string }> = ({
  segs, size = 110, thick = 13, center, csub,
}) => {
  const r = (size-thick)/2, circ = 2*Math.PI*r, total = segs.reduce((s,x)=>s+x.val,0)||1;
  const cx = size/2;
  let off = -90;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <circle cx={cx} cy={cx} r={r} fill="none" stroke="#f1f5f9" strokeWidth={thick} />
      {segs.map((s,i) => {
        const dash=(s.val/total)*circ, rotate=off;
        off += (s.val/total)*360;
        return s.val > 0 ? (
          <circle key={i} cx={cx} cy={cx} r={r} fill="none" stroke={s.color} strokeWidth={thick}
            strokeDasharray={`${dash} ${circ-dash}`} strokeLinecap="round"
            transform={`rotate(${rotate} ${cx} ${cx})`}
            style={{ transition:'stroke-dasharray .8s ease' }} />
        ) : null;
      })}
      {center && <text x={cx} y={cx-(csub?6:0)} textAnchor="middle" fontSize={size*.18} fontWeight="900" fill="#0f172a" fontFamily="Plus Jakarta Sans,Inter,sans-serif">{center}</text>}
      {csub   && <text x={cx} y={cx+size*.14} textAnchor="middle" fontSize={size*.09} fontWeight="700" fill="#94a3b8" fontFamily="Plus Jakarta Sans,Inter,sans-serif">{csub}</text>}
    </svg>
  );
};

/* Sales Overview Chart — stacked SVG bar chart */
const OverviewChart: React.FC<{
  weeks: SpWeekData[]; onBarClick: (w: SpWeekData) => void;
}> = ({ weeks, onBarClick }) => {
  const shown = weeks.slice(0, 8).reverse();
  if (shown.length === 0) return null;
  const maxVal = Math.max(...shown.map(w => w.incentive + w.pendingIncentive), 1);
  const W = 340, H = 120, BAR_W = 30, GAP = 12;
  const totalW = shown.length * (BAR_W + GAP) - GAP;
  const startX = (W - totalW) / 2;

  return (
    <div style={{ overflowX:'auto', width:'100%' }}>
      <svg width={W} height={H + 32} viewBox={`0 0 ${W} ${H + 32}`}
        style={{ display:'block', minWidth:W, maxWidth:'100%', margin:'0 auto' }}>
        {shown.map((w, i) => {
          const x      = startX + i * (BAR_W + GAP);
          const given  = w.payments.filter(p=>p.isFullyPaid && p.incentivePaid).reduce((s,p)=>s+p.incentive,0);
          const ungiven= w.incentive - given;
          const pending= w.pendingIncentive;

          const toH    = (v: number) => Math.round((v / maxVal) * H);
          const hGiven  = toH(given);
          const hUngiven= toH(ungiven);
          const hPending= toH(pending);
          const totalH  = hGiven + hUngiven + hPending;

          const yGiven  = H - hGiven;
          const yUngiven= H - hGiven - hUngiven;
          const yPending= H - totalH;

          const label = w.weekLabel.split(/[–-]/)[0].trim();
          const isNow = w.isCurrentWeek;

          return (
            <g key={w.weekKey} onClick={() => onBarClick(w)} style={{ cursor:'pointer' }}>
              {/* Track */}
              <rect x={x} y={0} width={BAR_W} height={H} rx={6} fill="#f1f5f9" />
              {/* Pending (blue) — bottom of remaining */}
              {hPending > 0 && <rect x={x} y={yPending} width={BAR_W} height={hPending} rx={hGiven===0&&hUngiven===0?6:0} fill="#7dd3fc" />}
              {/* Earned, not given (amber) — middle */}
              {hUngiven > 0 && <rect x={x} y={yUngiven} width={BAR_W} height={hUngiven} rx={hGiven===0?6:0} fill="#fcd34d" />}
              {/* Given (green) — top */}
              {hGiven > 0 && <rect x={x} y={yGiven} width={BAR_W} height={hGiven} rx={6} fill="#4ade80" />}
              {/* Now indicator */}
              {isNow && <rect x={x} y={H-2} width={BAR_W} height={3} rx={2} fill="#6d28d9" />}
              {/* Label */}
              <text x={x + BAR_W/2} y={H + 18} textAnchor="middle"
                fontSize="9" fontWeight={isNow ? '900' : '600'}
                fill={isNow ? '#6d28d9' : '#94a3b8'}
                fontFamily="Plus Jakarta Sans,Inter,sans-serif">
                {label}
              </text>
              {/* Total amount on hover — show on top bar */}
              {totalH > 0 && (
                <text x={x + BAR_W/2} y={yPending > 0 ? yPending - 4 : yUngiven > 0 ? yUngiven - 4 : yGiven - 4}
                  textAnchor="middle" fontSize="8" fontWeight="700" fill="#64748b"
                  fontFamily="Plus Jakarta Sans,Inter,sans-serif">
                  {w.incentive > 999 ? `${Math.round(w.incentive/1000)}k` : w.incentive}
                </text>
              )}
            </g>
          );
        })}
        {/* Legend */}
        {[
          { color:'#4ade80', label:'Given ✓' },
          { color:'#fcd34d', label:'Earned' },
          { color:'#7dd3fc', label:'Pending' },
        ].map((l,i) => (
          <g key={l.label} transform={`translate(${startX + i * 80}, ${H + 26})`}>
            <rect width={10} height={6} rx={3} y={1} fill={l.color} />
            <text x={14} y={8} fontSize="8" fontWeight="700" fill="#94a3b8"
              fontFamily="Plus Jakarta Sans,Inter,sans-serif">{l.label}</text>
          </g>
        ))}
      </svg>
    </div>
  );
};

/* Incentive status badge */
const IncBadge: React.FC<{ status: keyof typeof INC; pulse?: boolean }> = ({ status, pulse }) => {
  const c = INC[status];
  return (
    <span className={`status-chip ${pulse && (status==='given'||status==='notGiven') ? status+'-pulse' : ''}`}
      style={{ background:c.bg, color:c.text, borderColor:c.border,
        fontSize:10, padding:'2px 7px', lineHeight:1.3 }}>
      {c.icon}&nbsp;{c.label}
    </span>
  );
};

/* Payment row */
const PayRow: React.FC<{ p: SpPayment; rank: number; pct: number; last?: boolean }> = ({ p, rank, pct, last }) => {
  const st  = incStatus(p);
  const ci  = INC[st];
  const inc = p.isFullyPaid ? p.incentive : p.isPartial ? Math.round((p.courseFee||p.totalPaid)*pct/100) : 0;
  return (
    <div className="pay-row" style={{
      display:'flex', alignItems:'center', gap:10, padding:'11px 16px',
      borderBottom: last ? 'none' : '1px solid #f1f5f9',
      borderLeft: `3px solid ${ci.border}`,
      background: p.incentivePaid ? 'rgba(220,252,231,.25)' : undefined,
    }}>
      {/* Rank bubble */}
      <span style={{
        width:26, height:26, borderRadius:'50%', flexShrink:0, fontSize:11, fontWeight:900,
        display:'flex', alignItems:'center', justifyContent:'center',
        background:ci.bg, color:ci.text,
      }}>
        {p.isRunaway ? '✕' : rank}
      </span>
      {/* Name / course */}
      <div style={{ flex:1, minWidth:0 }}>
        <p className="truncate" style={{ fontSize:13, fontWeight:700, color:'#0f172a', lineHeight:1.2 }}>
          {p.studentName}
        </p>
        <p className="truncate" style={{ fontSize:11, color:'#94a3b8', marginTop:1 }}>{p.course}</p>
      </div>
      {/* Amount paid */}
      <div style={{ textAlign:'right', flexShrink:0 }}>
        <p style={{ fontSize:13, fontWeight:800, color:'#0f172a' }}>{fmtINR(p.totalPaid)}</p>
        {p.courseFee > p.totalPaid && (
          <p style={{ fontSize:10, color:'#94a3b8', marginTop:1 }}>of {fmtINR(p.courseFee)}</p>
        )}
      </div>
      {/* Status + incentive */}
      <div style={{ textAlign:'right', flexShrink:0, minWidth:90 }}>
        <IncBadge status={st} pulse />
        {p.isFullyPaid && (
          <p style={{ fontSize:12, fontWeight:900, marginTop:4,
            color: p.incentivePaid ? '#15803d' : '#92400e' }}>
            {fmtINR(inc)}
            {p.incentivePaid
              ? <span style={{ fontSize:9, marginLeft:3, opacity:.7 }}>✓ given</span>
              : <span style={{ fontSize:9, marginLeft:3, opacity:.7 }}>awaiting</span>}
          </p>
        )}
        {p.isPartial && inc > 0 && (
          <p style={{ fontSize:10, color:'#075985', marginTop:4, fontWeight:700 }}>
            {fmtINR(inc)} if paid
          </p>
        )}
      </div>
    </div>
  );
};

/* Drawer */
const Drawer: React.FC<{ week: SpWeekData; onClose: () => void }> = ({ week, onClose }) => {
  const ti = getTierInfo(week.tier.name);
  const groups = [
    {
      key:'given', title:'✅ Incentive Given by Admin', color:'#15803d',
      list: week.payments.filter(p => p.isFullyPaid && p.incentivePaid),
    },
    {
      key:'notGiven', title:'💰 Earned — Awaiting Payment', color:'#92400e',
      list: week.payments.filter(p => p.isFullyPaid && !p.incentivePaid),
    },
    {
      key:'partial', title:'⏳ Partial — Awaiting Full Payment', color:'#075985',
      list: week.payments.filter(p => p.isPartial),
    },
    {
      key:'runaway', title:'❌ Ran Away', color:'#991b1b',
      list: week.payments.filter(p => p.isRunaway),
    },
  ].filter(g => g.list.length > 0);

  return (
    <>
      <div className="fixed inset-0 z-40" onClick={onClose}
        style={{ background:'rgba(2,6,23,.55)', backdropFilter:'blur(6px)', WebkitBackdropFilter:'blur(6px)' }} />
      <div className="fixed inset-x-0 bottom-0 z-50 flex flex-col rounded-t-3xl"
        style={{ maxHeight:'90vh', background:'#fff', animation:'slideUp .28s cubic-bezier(.32,.72,0,1)',
          boxShadow:'0 -12px 56px rgba(0,0,0,.22)', overflow:'hidden' }}>
        {/* Handle */}
        <div style={{ display:'flex', justifyContent:'center', padding:'10px 0 2px', flexShrink:0 }}>
          <div style={{ width:36, height:4, borderRadius:99, background:'#e2e8f0' }} />
        </div>
        {/* Gradient header */}
        <div style={{ background:ti.grad, padding:'14px 20px 18px', flexShrink:0 }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
            <div>
              <p style={{ fontSize:10, color:'rgba(255,255,255,.75)', fontWeight:700, textTransform:'uppercase', letterSpacing:'.07em' }}>
                {week.weekRange}
              </p>
              <p style={{ fontSize:20, fontWeight:900, color:'#fff', letterSpacing:'-.02em', marginTop:4 }}>
                {week.tier.name} · {week.tier.pct}%
              </p>
            </div>
            <button onClick={onClose} style={{
              width:30, height:30, borderRadius:'50%', background:'rgba(255,255,255,.22)',
              border:'none', color:'#fff', cursor:'pointer', fontSize:18, display:'flex',
              alignItems:'center', justifyContent:'center',
            }}>×</button>
          </div>
          {/* Summary pills */}
          <div style={{ display:'flex', gap:8, marginTop:12, flexWrap:'wrap' }}>
            {[
              { label:'Given',    val:fmtINR(week.payments.filter(p=>p.isFullyPaid&&p.incentivePaid).reduce((s,p)=>s+p.incentive,0)), bg:'rgba(74,222,128,.35)', show:true },
              { label:'Awaiting', val:fmtINR(week.payments.filter(p=>p.isFullyPaid&&!p.incentivePaid).reduce((s,p)=>s+p.incentive,0)), bg:'rgba(252,211,77,.35)', show:week.payments.some(p=>p.isFullyPaid&&!p.incentivePaid) },
              { label:'Pending',  val:fmtINR(week.pendingIncentive), bg:'rgba(125,211,252,.3)', show:week.pendingIncentive>0 },
              { label:'Students', val:String(week.payments.length),   bg:'rgba(255,255,255,.2)', show:true },
            ].filter(x=>x.show).map(x=>(
              <div key={x.label} style={{ background:x.bg, borderRadius:10, padding:'5px 11px' }}>
                <p style={{ fontSize:9,  color:'rgba(255,255,255,.8)', fontWeight:700, textTransform:'uppercase', letterSpacing:'.05em' }}>{x.label}</p>
                <p style={{ fontSize:16, color:'#fff', fontWeight:900, lineHeight:1.1, marginTop:1 }}>{x.val}</p>
              </div>
            ))}
          </div>
        </div>
        {/* Groups */}
        <div style={{ flex:1, overflowY:'auto' }}>
          {groups.map(g => (
            <div key={g.key}>
              <div style={{ padding:'9px 16px 4px', background:'#fafafa', borderBottom:'1px solid #f1f5f9' }}>
                <p style={{ fontSize:11, fontWeight:800, color:g.color, textTransform:'uppercase', letterSpacing:'.07em' }}>
                  {g.title}
                </p>
              </div>
              {g.list.map((p,i) => <PayRow key={p.id} p={p} rank={i+1} pct={week.tier.pct} last={i===g.list.length-1} />)}
            </div>
          ))}
          {week.payments.length === 0 && (
            <p style={{ textAlign:'center', color:'#94a3b8', padding:'40px', fontSize:14 }}>No payments this week</p>
          )}
          <div style={{ height:24 }} />
        </div>
      </div>
    </>
  );
};

/* ─── Motivational Hero ────────────────────────────────────────────────────── */
const GOAL_THEMES: Record<string, string> = {
  '#6d28d9': 'linear-gradient(135deg,#6d28d9 0%,#a855f7 50%,#ec4899 100%)',
  '#d97706': 'linear-gradient(135deg,#f59e0b 0%,#ef4444 60%,#ec4899 100%)',
  '#0ea5e9': 'linear-gradient(135deg,#0ea5e9 0%,#6366f1 50%,#8b5cf6 100%)',
  '#16a34a': 'linear-gradient(135deg,#16a34a 0%,#0ea5e9 50%,#6366f1 100%)',
  '#dc2626': 'linear-gradient(135deg,#dc2626 0%,#f97316 50%,#fbbf24 100%)',
  '#1e1b4b': 'linear-gradient(135deg,#1e1b4b 0%,#312e81 50%,#4f46e5 100%)',
};

const Confetti: React.FC = () => {
  const pieces = Array.from({ length: 14 }, (_, i) => ({
    x: Math.random() * 100,
    color: ['#fbbf24','#f87171','#a78bfa','#34d399','#60a5fa','#fb923c'][i % 6],
    delay: (Math.random() * 1.5).toFixed(2),
    dur: (1.2 + Math.random()).toFixed(2),
    size: 5 + Math.random() * 6,
  }));
  return (
    <div style={{ position:'absolute', inset:0, pointerEvents:'none', overflow:'hidden' }}>
      {pieces.map((p, i) => (
        <div key={i} style={{
          position:'absolute', left:`${p.x}%`, top:0,
          width:p.size, height:p.size, borderRadius:2,
          background:p.color, opacity:.9,
          animation:`confetti ${p.dur}s ${p.delay}s ease-in infinite`,
        }} />
      ))}
    </div>
  );
};

const MotivationHero: React.FC<{
  goal: SalesGoalData; spName: string; salesCount: number;
}> = ({ goal, spName, salesCount }) => {
  const grad = GOAL_THEMES[goal.themeColor] || GOAL_THEMES['#6d28d9'];
  const target = goal.weeklyTarget || 10;
  const pct    = Math.min(100, Math.round(salesCount / target * 100));
  const goalMet = pct >= 100;
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 80);
    return () => clearTimeout(t);
  }, []);

  return (
    <div style={{
      borderRadius:24, overflow:'hidden', marginBottom:20, position:'relative',
      background: grad,
      backgroundSize:'200% 200%',
      animation:'gradShift 6s ease infinite',
      boxShadow:`0 16px 48px ${goal.themeColor}55`,
      opacity: visible ? 1 : 0,
      transform: visible ? 'none' : 'translateY(20px)',
      transition:'opacity .5s ease, transform .5s ease',
    }}>
      {/* Floating orbs */}
      <div style={{ position:'absolute', width:220, height:220, borderRadius:'50%',
        background:'rgba(255,255,255,.08)', top:-70, right:-60,
        animation:'floatOrb 5s ease-in-out infinite', pointerEvents:'none' }} />
      <div style={{ position:'absolute', width:130, height:130, borderRadius:'50%',
        background:'rgba(255,255,255,.06)', bottom:-40, left:-30,
        animation:'floatOrb2 4s ease-in-out infinite', pointerEvents:'none' }} />
      <div style={{ position:'absolute', width:70, height:70, borderRadius:'50%',
        background:'rgba(255,255,255,.08)', top:'40%', left:'40%',
        animation:'floatOrb 7s 1s ease-in-out infinite', pointerEvents:'none' }} />

      {/* Confetti if goal met */}
      {goalMet && <Confetti />}

      <div style={{ position:'relative', padding:'24px 20px 20px' }}>
        {/* Top row: emoji + badge */}
        <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:12 }}>
          <span style={{ fontSize:42, lineHeight:1,
            filter:'drop-shadow(0 4px 12px rgba(0,0,0,.35))',
            animation:'floatOrb2 3s ease-in-out infinite',
            display:'inline-block',
          }}>
            {goalMet ? '🏆' : goal.emoji}
          </span>
          {goal.badge && (
            <div style={{
              background:'rgba(255,255,255,.18)', backdropFilter:'blur(10px)',
              borderRadius:99, padding:'5px 13px',
              border:'1px solid rgba(255,255,255,.35)',
            }}>
              <p style={{ fontSize:10, fontWeight:800, color:'#fff',
                textTransform:'uppercase', letterSpacing:'.07em' }}>
                {goal.badge}
              </p>
            </div>
          )}
        </div>

        {/* Greeting */}
        <p style={{ fontSize:11, color:'rgba(255,255,255,.75)', fontWeight:700,
          textTransform:'uppercase', letterSpacing:'.12em', marginBottom:5 }}>
          Hey {spName.split(' ')[0]} 👋
        </p>

        {/* Headline */}
        <h2 style={{
          fontSize: 22,
          fontWeight:900, color:'#fff', lineHeight:1.18,
          letterSpacing:'-.02em', marginBottom:10,
          textShadow:'0 2px 16px rgba(0,0,0,.3)',
        }}>
          {goalMet
            ? `🎉 Goal Crushed! You\'re a Champion!`
            : (goal.headline || 'Let\'s make this week legendary!')}
        </h2>

        {/* Message */}
        {goal.message && !goalMet && (
          <p style={{
            fontSize:13, color:'rgba(255,255,255,.82)', lineHeight:1.65,
            marginBottom:16, fontWeight:500,
            display:'-webkit-box', WebkitLineClamp:3,
            WebkitBoxOrient:'vertical' as any, overflow:'hidden',
          }}>
            {goal.message}
          </p>
        )}
        {goalMet && (
          <p style={{ fontSize:13, color:'rgba(255,255,255,.9)', lineHeight:1.6,
            marginBottom:16, fontWeight:600 }}>
            Incredible work! You've hit your weekly target of {target} sales.
            You're setting the standard for the entire team! 🌟
          </p>
        )}

        {/* Difficulty + Challenge + Reward chips */}
        {(goal.difficulty || goal.challenge || goal.reward) && (
          <div style={{ display:'flex', flexWrap:'wrap', gap:8, marginBottom:14 }}>
            {goal.difficulty && goal.difficulty !== 'Standard' && (
              <div style={{ display:'flex', alignItems:'center', gap:5, background:'rgba(255,255,255,.18)', backdropFilter:'blur(8px)', borderRadius:99, padding:'5px 12px', border:'1px solid rgba(255,255,255,.25)' }}>
                <span style={{ fontSize:12 }}>{goal.difficulty==='Extreme'?'🔴':goal.difficulty==='Advanced'?'🟠':goal.difficulty==='Easy'?'🟢':'🔵'}</span>
                <span style={{ fontSize:11, fontWeight:800, color:'#fff', textTransform:'uppercase', letterSpacing:'.05em' }}>{goal.difficulty}</span>
              </div>
            )}
            {goal.challenge && (
              <div style={{ display:'flex', alignItems:'center', gap:5, background:'rgba(255,255,255,.14)', backdropFilter:'blur(8px)', borderRadius:12, padding:'7px 12px', border:'1px solid rgba(255,255,255,.2)', flex:1, minWidth:0 }}>
                <span style={{ fontSize:13, flexShrink:0 }}>🎯</span>
                <div style={{ minWidth:0 }}>
                  <p style={{ fontSize:9, color:'rgba(255,255,255,.6)', fontWeight:800, textTransform:'uppercase', letterSpacing:'.07em' }}>Your Challenge</p>
                  <p style={{ fontSize:11, color:'#fff', fontWeight:700, lineHeight:1.4 }}>{goal.challenge}</p>
                </div>
              </div>
            )}
            {goal.reward && (
              <div style={{ display:'flex', alignItems:'center', gap:5, background:'rgba(251,191,36,.2)', backdropFilter:'blur(8px)', borderRadius:12, padding:'7px 12px', border:'1px solid rgba(251,191,36,.35)', flex:1, minWidth:0 }}>
                <span style={{ fontSize:13, flexShrink:0 }}>🏅</span>
                <div style={{ minWidth:0 }}>
                  <p style={{ fontSize:9, color:'rgba(251,191,36,.85)', fontWeight:800, textTransform:'uppercase', letterSpacing:'.07em' }}>Your Reward</p>
                  <p style={{ fontSize:11, color:'#fef3c7', fontWeight:700, lineHeight:1.4 }}>{goal.reward}</p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Goal progress bar */}
        <div style={{
          background:'rgba(0,0,0,.22)', backdropFilter:'blur(8px)',
          borderRadius:16, padding:'14px 16px',
          border:'1px solid rgba(255,255,255,.15)',
        }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10 }}>
            <div>
              <p style={{ fontSize:10, color:'rgba(255,255,255,.7)', fontWeight:700,
                textTransform:'uppercase', letterSpacing:'.08em' }}>
                Weekly Goal Progress
              </p>
              <p style={{ fontSize:12, color:'rgba(255,255,255,.6)', marginTop:2 }}>
                {salesCount} of {target} this week
              </p>
            </div>
            <div style={{ textAlign:'right' }}>
              <p style={{ fontSize:30, fontWeight:900, color:'#fff', lineHeight:1,
                textShadow:'0 2px 8px rgba(0,0,0,.3)' }}>
                {pct}%
              </p>
              <p style={{ fontSize:10, color:'rgba(255,255,255,.6)' }}>complete</p>
            </div>
          </div>
          {/* Animated progress bar */}
          <div style={{ height:10, borderRadius:99, background:'rgba(255,255,255,.18)', overflow:'hidden' }}>
            <div style={{
              height:'100%', borderRadius:99,
              background: goalMet
                ? 'linear-gradient(90deg,#fbbf24,#f59e0b)'
                : 'rgba(255,255,255,.9)',
              '--target-w': `${pct}%`,
              animation: 'progressFill 1.4s cubic-bezier(.34,1.56,.64,1) forwards',
              boxShadow: goalMet ? '0 0 12px rgba(251,191,36,.8)' : '0 0 8px rgba(255,255,255,.5)',
            } as React.CSSProperties} />
          </div>
          {/* Milestone markers */}
          <div style={{ display:'flex', justifyContent:'space-between', marginTop:6 }}>
            {[25, 50, 75, 100].map(m => (
              <div key={m} style={{ textAlign:'center' }}>
                <div style={{
                  width:1, height:5, background:'rgba(255,255,255,.3)',
                  margin:'0 auto 3px',
                }} />
                <p style={{ fontSize:9, color: pct >= m ? 'rgba(255,255,255,.9)' : 'rgba(255,255,255,.4)',
                  fontWeight: pct >= m ? 800 : 600, transition:'color .3s' }}>
                  {m}%
                </p>
              </div>
            ))}
          </div>
          {/* Remaining / achieved callout */}
          {!goalMet && target - salesCount > 0 && (
            <div style={{
              marginTop:14, borderRadius:14, padding:'14px 16px',
              background:'rgba(255,255,255,.13)',
              border:'1.5px solid rgba(255,255,255,.25)',
              backdropFilter:'blur(6px)',
              textAlign:'center',
              animation:'motivePulse 2.4s ease-in-out infinite',
            }}>
              <p style={{ fontSize:38, fontWeight:900, color:'#fff', lineHeight:1,
                textShadow:'0 4px 20px rgba(0,0,0,.35)', letterSpacing:'-.02em' }}>
                {target - salesCount}
                <span style={{ fontSize:16, fontWeight:700, marginLeft:6, opacity:.8 }}>
                  more {target - salesCount === 1 ? 'sale' : 'sales'}
                </span>
              </p>
              <p style={{ fontSize:15, fontWeight:800, color:'rgba(255,255,255,.92)',
                marginTop:5, letterSpacing:'.01em' }}>
                to CRUSH your goal! 🔥
              </p>
              <p style={{ fontSize:11, color:'rgba(255,255,255,.6)', marginTop:4, fontWeight:600 }}>
                You've got this {'—'} keep pushing!
              </p>
            </div>
          )}
          {!goalMet && target - salesCount <= 0 && (
            <div style={{ marginTop:14, borderRadius:14, padding:'12px 16px',
              background:'rgba(251,191,36,.2)', border:'1.5px solid rgba(251,191,36,.4)',
              textAlign:'center' }}>
              <p style={{ fontSize:18, fontWeight:900, color:'#fde68a' }}>Almost there! ⚡ One more push!</p>
            </div>
          )}
          {goalMet && (
            <div style={{ marginTop:14, borderRadius:14, padding:'14px 16px',
              background:'rgba(74,222,128,.18)', border:'1.5px solid rgba(74,222,128,.4)',
              textAlign:'center', animation:'motivePulse 2s ease-in-out infinite' }}>
              <p style={{ fontSize:22, fontWeight:900, color:'#bbf7d0',
                textShadow:'0 2px 12px rgba(74,222,128,.5)' }}>
                🏆 TARGET ACHIEVED!
              </p>
              <p style={{ fontSize:12, color:'rgba(255,255,255,.7)', marginTop:4, fontWeight:700 }}>
                Share this moment with your team! ✨
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/* ─── Daily Quote Card ─────────────────────────────────────────────────────── */
// 30 built-in fallback quotes (used if DB is empty)
const BUILTIN_QUOTES = [
  { quote: "The secret of getting ahead is getting started.", author: "Mark Twain" },
  { quote: "Don't watch the clock; do what it does. Keep going.", author: "Sam Levenson" },
  { quote: "Success is not final, failure is not fatal: it is the courage to continue that counts.", author: "Winston Churchill" },
  { quote: "Believe you can and you're halfway there.", author: "Theodore Roosevelt" },
  { quote: "It always seems impossible until it's done.", author: "Nelson Mandela" },
  { quote: "The only way to do great work is to love what you do.", author: "Steve Jobs" },
  { quote: "Opportunities don't happen. You create them.", author: "Chris Grosser" },
  { quote: "Great things never come from comfort zones.", author: "Unknown" },
  { quote: "Push yourself, because no one else is going to do it for you.", author: "Unknown" },
  { quote: "Success doesn't just find you. You have to go out and get it.", author: "Unknown" },
  { quote: "Wake up with determination. Go to bed with satisfaction.", author: "Unknown" },
  { quote: "Dream it. Believe it. Build it.", author: "Unknown" },
  { quote: "Little things make big days.", author: "Unknown" },
  { quote: "It's going to be hard, but hard is not impossible.", author: "Unknown" },
  { quote: "Don't stop when you're tired. Stop when you're done.", author: "Unknown" },
  { quote: "The harder you work for something, the greater you'll feel when you achieve it.", author: "Unknown" },
  { quote: "Champions keep playing until they get it right.", author: "Billie Jean King" },
  { quote: "You don't have to be great to start, but you have to start to be great.", author: "Zig Ziglar" },
  { quote: "A winner is a dreamer who never gives up.", author: "Nelson Mandela" },
  { quote: "If you want something you've never had, do something you've never done.", author: "Unknown" },
  { quote: "Small steps every day add up to big results.", author: "Unknown" },
  { quote: "Your attitude determines your direction.", author: "Unknown" },
  { quote: "Energy and persistence conquer all things.", author: "Benjamin Franklin" },
  { quote: "You are stronger than you think.", author: "Unknown" },
  { quote: "One sale at a time. One goal at a time. One day at a time.", author: "Unknown" },
  { quote: "Excellence is not a destination but a continuous journey.", author: "Brian Tracy" },
  { quote: "The difference between ordinary and extraordinary is that little extra.", author: "Jimmy Johnson" },
  { quote: "Act as if what you do makes a difference. It does.", author: "William James" },
  { quote: "Don't limit your challenges. Challenge your limits.", author: "Unknown" },
  { quote: "Your only limit is your mind.", author: "Unknown" },
];

function getTodayBuiltinQuote() {
  const d = new Date();
  const dayOfYear = Math.floor((d.getTime() - new Date(d.getFullYear(), 0, 0).getTime()) / 86400000);
  return BUILTIN_QUOTES[dayOfYear % BUILTIN_QUOTES.length];
}

const DailyQuoteCard: React.FC<{ todayQ: { quote: string; author: string } | null }> = ({ todayQ }) => {
  const q = todayQ || getTodayBuiltinQuote();
  const day = new Date().toLocaleDateString('en-IN', { weekday:'long', day:'numeric', month:'long' });
  const [show, setShow] = useState(false);
  useEffect(() => { const t = setTimeout(() => setShow(true), 200); return () => clearTimeout(t); }, []);

  return (
    <div style={{
      marginBottom: 18, borderRadius: 20, overflow: 'hidden',
      background: 'linear-gradient(135deg,#0f172a 0%,#1e1b4b 50%,#312e81 100%)',
      boxShadow: '0 8px 32px rgba(15,23,42,.35)',
      opacity: show ? 1 : 0, transform: show ? 'none' : 'translateY(12px)',
      transition: 'opacity .5s .1s ease, transform .5s .1s ease',
      position: 'relative',
    }}>
      {/* Star particles */}
      {[...Array(8)].map((_, i) => (
        <div key={i} style={{
          position: 'absolute',
          width: 3 + (i % 3), height: 3 + (i % 3),
          borderRadius: '50%',
          background: ['#fbbf24','#a78bfa','#60a5fa','#34d399'][i % 4],
          top: `${10 + (i * 11) % 70}%`,
          left: `${5 + (i * 13) % 90}%`,
          opacity: 0.6,
          animation: `sparkle ${2 + i * 0.3}s ${i * 0.4}s ease-in-out infinite`,
        }} />
      ))}

      <div style={{ position: 'relative', padding: '20px 20px 18px' }}>
        {/* Header row */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
            <span style={{ fontSize: 18 }}>✨</span>
            <div>
              <p style={{ fontSize: 9, color: '#818cf8', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.1em' }}>
                Quote of the Day
              </p>
              <p style={{ fontSize: 10, color: '#64748b', fontWeight: 600, marginTop: 1 }}>{day}</p>
            </div>
          </div>
          <div style={{
            background: 'rgba(129,140,248,.15)', border: '1px solid rgba(129,140,248,.3)',
            borderRadius: 99, padding: '3px 10px',
          }}>
            <p style={{ fontSize: 9, color: '#818cf8', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.07em' }}>
              Daily
            </p>
          </div>
        </div>

        {/* Quote marks */}
        <div style={{ fontSize: 48, lineHeight: 1, color: '#4f46e5', opacity: .5,
          fontFamily: 'Georgia,serif', marginBottom: -10, marginLeft: -4 }}>"</div>

        {/* Quote text */}
        <p style={{
          fontSize: 15, fontWeight: 700, color: '#e2e8f0', lineHeight: 1.6,
          letterSpacing: '-.01em', marginBottom: 10,
          fontStyle: 'italic',
        }}>
          {q.quote}
        </p>

        {/* Author */}
        {q.author && (
          <p style={{ fontSize: 12, fontWeight: 800, color: '#818cf8', textAlign: 'right' }}>
            — {q.author}
          </p>
        )}
      </div>

      {/* Bottom glow line */}
      <div style={{
        height: 3,
        background: 'linear-gradient(90deg,#6366f1,#a855f7,#ec4899,#f59e0b,#6366f1)',
        backgroundSize: '200%',
        animation: 'gradShift 3s linear infinite',
      }} />
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────────────── */
/*  AVAILABLE SLOTS TAB                                                        */
/* ─────────────────────────────────────────────────────────────────────────── */
// Grouped by course — each course has its own times. Slots added before that
// have no course and are offered for both.
const slotGroups = (slots: SessionSlot[]) => [
  ...SLOT_COURSES.map(c => ({ key: c.value, title: c.label, list: slots.filter(s => s.course === c.value) })),
  { key: 'both', title: 'Both courses', list: slots.filter(s => !SLOT_COURSES.some(c => c.value === s.course)) },
].filter(g => g.list.length > 0);

const SlotsTab: React.FC = () => {
  const [slots,   setSlots]   = useState<SessionSlot[] | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getPublicSessionSlots();
      setSlots(res.ok ? res.slots : []);
    } catch { setSlots([]); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="fu1" style={{ maxWidth: 560, margin: '0 auto', paddingBottom: 48 }}>
      <div style={{ textAlign: 'center', marginBottom: 20 }}>
        <div style={{ fontSize: 40, marginBottom: 6 }}>🕐</div>
        <h2 style={{ fontSize: 22, fontWeight: 900, color: '#0f172a', margin: 0 }}>Available Time Slots</h2>
        <p style={{ fontSize: 12, color: '#94a3b8', marginTop: 4 }}>Only tell students these are open right now — the list updates as admin changes it.</p>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8', fontSize: 13 }}>Loading…</div>
      ) : !slots || slots.length === 0 ? (
        <div style={{ background: '#fff', borderRadius: 20, padding: '32px 20px', boxShadow: '0 2px 12px rgba(0,0,0,.07)', textAlign: 'center' }}>
          <p style={{ fontSize: 13, color: '#94a3b8', margin: 0 }}>No slots are open right now — check back later, or ask admin.</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          {slotGroups(slots).map(g => (
            <div key={g.key}>
              <p style={{ fontSize: 12, fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.06em', margin: '0 0 8px 4px' }}>
                {g.title} <span style={{ color: '#94a3b8', fontWeight: 600 }}>· {g.list.length} open</span>
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {g.list.map(s => (
                  <div key={s._id} style={{ background: '#fff', borderRadius: 16, padding: '16px 20px', boxShadow: '0 2px 12px rgba(0,0,0,.07)' }}>
                    <span style={{ fontSize: 15, fontWeight: 800, color: '#0f172a' }}>{s.label}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <button onClick={load} style={{ display: 'block', margin: '16px auto 0', padding: '8px 16px', borderRadius: 10, border: '1px solid #e2e8f0', background: '#fff', color: '#64748b', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
        ↻ Refresh
      </button>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────────────── */
/*  EOD TAB                                                                    */
/* ─────────────────────────────────────────────────────────────────────────── */
const MOODS_EOD = [
  { label:'Great', emoji:'🚀', color:'#10B981' },
  { label:'Good',  emoji:'😊', color:'#3B82F6' },
  { label:'Okay',  emoji:'😐', color:'#F0B429' },
  { label:'Tough', emoji:'😓', color:'#EF4444' },
];

// Today's calls, counted from the sales CRM: the numbers fill themselves in
const EOD_NUMBERS: [keyof EodCounts, string][] = [
  ['calls', 'Calls'], ['pitched', 'Brochures sent'], ['pollsSent', 'Polls sent'],
  ['interested', 'Interested'], ['notInterested', 'Not interested'], ['converted', 'Converted'],
];

const EodTab: React.FC<{ salesPersonId: string }> = ({ salesPersonId }) => {
  const [today,       setToday]       = useState(new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' }));
  const [counts,      setCounts]      = useState<EodCounts | null>(null);
  const [sentAuto,    setSentAuto]    = useState(false);
  const [mood,        setMood]        = useState('Good');
  const [highlights,  setHighlights]  = useState('');
  const [challenges,  setChallenges]  = useState('');
  const [plan,        setPlan]        = useState('');
  const [submitting,  setSubmitting]  = useState(false);
  const [success,     setSuccess]     = useState('');
  const [error,       setError]       = useState('');

  const loadToday = useCallback(async (fill: boolean) => {
    try {
      const r = await getMyEodToday(salesPersonId);
      setToday(r.date); setCounts(r.counts);
      setSentAuto(!!r.report?.auto);
      // A report sent earlier today opens with what they wrote
      if (fill && r.report && !r.report.auto) {
        setMood(r.report.mood || 'Good'); setHighlights(r.report.highlights || ''); setChallenges(r.report.challenges || ''); setPlan(r.report.plan || '');
      }
    } catch { /* the form still works; the server counts the calls on submit */ }
  }, [salesPersonId]);
  useEffect(() => { loadToday(true); }, [loadToday]);

  const handleSubmit = async () => {
    setSubmitting(true); setError(''); setSuccess('');
    try {
      const res = await submitEOD({ salesPersonId, callsMade: counts?.calls || 0, demosGiven: counts?.pitched || 0, conversions: counts?.converted || 0, mood, highlights: highlights.trim(), challenges: challenges.trim(), plan: plan.trim() });
      setSuccess(res.updated ? '✅ Report updated for today. Keep pushing! 💪' : '✅ Great work today! Your EOD has been sent to the team. 🚀');
      loadToday(false);
    } catch (e: any) { setError(e.message || 'Failed'); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="fu1" style={{ maxWidth:560, margin:'0 auto', paddingBottom:48 }}>

      {/* Header */}
      <div style={{ textAlign:'center', marginBottom:20 }}>
        <div style={{ fontSize:40, marginBottom:6 }}>📋</div>
        <h2 style={{ fontSize:22, fontWeight:900, color:'#0f172a', margin:0 }}>End of Day Report</h2>
        <p style={{ fontSize:12, color:'#94a3b8', marginTop:4 }}>{today}</p>
      </div>

      {/* Today's numbers from the CRM */}
      <div style={{ background:'#fff', borderRadius:20, padding:'16px 20px', boxShadow:'0 2px 12px rgba(0,0,0,.07)', marginBottom:14 }}>
        <p style={{ fontSize:13, fontWeight:700, color:'#64748b', marginBottom:10 }}>Today from the CRM</p>
        {!counts ? <p style={{ fontSize:12, color:'#94a3b8' }}>Counting today's calls…</p> : (
          <>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:8 }}>
              {EOD_NUMBERS.map(([k, label]) => (
                <div key={k} style={{ textAlign:'center', background:'#f8fafc', borderRadius:12, padding:'10px 4px' }}>
                  <div style={{ fontSize:22, fontWeight:900, color:'#312741' }}>{counts[k] ?? 0}</div>
                  <div style={{ fontSize:10, fontWeight:700, color:'#94a3b8', textTransform:'uppercase', letterSpacing:'.04em' }}>{label}</div>
                </div>
              ))}
            </div>
            <p style={{ fontSize:11, color:'#94a3b8', marginTop:8 }}>
              Talked to {counts.talked} · Not picked {counts.notPicked} · Call later {counts.callLater} · Cold {counts.cold} · No response {counts.noResponse}
            </p>
          </>
        )}
        <p style={{ fontSize:11, color:'#94a3b8', marginTop:6 }}>
          {sentAuto ? 'Your report was made automatically at the end of the day. Add how your day went below to send it as yours.'
            : 'These fill in by themselves. If you don’t send a report, one is made with these numbers at the end of the working day.'}
        </p>
      </div>

      {/* Mood */}
      <div style={{ background:'#fff', borderRadius:20, padding:'18px 20px', boxShadow:'0 2px 12px rgba(0,0,0,.07)', marginBottom:14 }}>
        <p style={{ fontSize:13, fontWeight:700, color:'#64748b', marginBottom:12 }}>How was your day?</p>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:8 }}>
          {MOODS_EOD.map(m => (
            <button key={m.label} onClick={() => setMood(m.label)}
              style={{ padding:'14px 4px', borderRadius:14, border:`2px solid ${mood===m.label ? m.color : '#e5e7eb'}`,
                background: mood===m.label ? `${m.color}18` : '#fff', cursor:'pointer', transition:'all .15s' }}>
              <div style={{ fontSize:26 }}>{m.emoji}</div>
              <div style={{ fontSize:11, fontWeight:700, color: mood===m.label ? m.color : '#9ca3af', marginTop:6 }}>{m.label}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Text areas */}
      {[
        { label:'🌟 End Of Day',         placeholder:'What went well today? Any wins?',      value:highlights, onChange:setHighlights, color:'#10B981' },
        { label:'⚠️ Challenges',          placeholder:'What was difficult? Any blockers?',    value:challenges, onChange:setChallenges, color:'#F97316' },
        { label:'📋 Plan for Tomorrow',  placeholder:'What will you focus on tomorrow?',      value:plan,       onChange:setPlan,       color:'#3B82F6' },
      ].map(({ label, placeholder, value, onChange, color }) => (
        <div key={label} style={{ background:'#fff', borderRadius:20, padding:'16px 20px', boxShadow:'0 2px 12px rgba(0,0,0,.07)', marginBottom:10 }}>
          <p style={{ fontSize:12, fontWeight:700, color, marginBottom:8 }}>{label}</p>
          <textarea rows={3} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
            style={{ width:'100%', fontSize:13, border:`1.5px solid ${color}40`, borderRadius:10, padding:'10px 12px',
              color:'#374151', resize:'none', fontFamily:'inherit', outline:'none', boxSizing:'border-box' }} />
        </div>
      ))}

      {error   && <div style={{ background:'#fee2e2', border:'1px solid #fca5a5', color:'#991b1b', borderRadius:12, padding:'12px 16px', fontSize:13, marginBottom:12 }}>{error}</div>}
      {success && <div style={{ background:'#dcfce7', border:'1px solid #86efac', color:'#166534', borderRadius:12, padding:'12px 16px', fontSize:13, marginBottom:12 }}>{success}</div>}

      <button onClick={handleSubmit} disabled={submitting}
        style={{ width:'100%', padding:'15px', borderRadius:16, border:'none', cursor: submitting ? 'not-allowed' : 'pointer',
          background: submitting ? '#9ca3af' : 'linear-gradient(135deg,#312741,#5b21b6)',
          color:'#fff', fontSize:15, fontWeight:800, letterSpacing:'.01em',
          boxShadow: submitting ? 'none' : '0 8px 24px rgba(91,33,182,.35)' }}>
        {submitting ? 'Submitting…' : 'Submit EOD Report'}
      </button>
      <p style={{ textAlign:'center', fontSize:11, color:'#94a3b8', marginTop:10 }}>A copy is emailed to vistatech admin</p>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────────────── */
/*  MAIN DASHBOARD                                                             */
/* ─────────────────────────────────────────────────────────────────────────── */
type Tab = 'overview' | 'weekly' | 'tiers' | 'slots' | 'eod' | 'leave';
type DashData = Awaited<ReturnType<typeof getSalesPersonDashboard>>;

const SalesDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { logout } = useTracker();
  const sp        = useSalesPersonSession();

  // The Sales CRM is its own site (/crm): open it already signed in
  const openCrm = () => {
    if (!sp.crmToken) { navigate('/crm/login'); return; }
    const session: CrmSession = {
      kind: 'sales', id: sp.salesPersonId, name: sp.name, role: sp.salesRole,
      isManager: sp.salesRole !== 'Salesperson', takesLeads: sp.takesLeads, crmToken: sp.crmToken,
    };
    forgetCrmMe();
    saveCrmSession(session);
    navigate(`/crm/${homePage(session)}`);
  };

  const [tab,       setTab]      = useState<Tab>('overview');
  const [data,      setData]     = useState<DashData | null>(null);
  const [loading,   setLoad]     = useState(true);
  const [drawer,    setDrawer]   = useState<SpWeekData | null>(null);
  const [mf,        setMf]       = useState('all');
  const [sideOpen,  setSide]     = useState(false);
  const [goal,      setGoal]     = useState<SalesGoalData | null>(null);
  const [todayQ,    setTodayQ]   = useState<{ quote: string; author: string } | null>(null);

  const load = useCallback(async () => {
    setLoad(true);
    try {
      const [dash, motiv] = await Promise.all([
        getSalesPersonDashboard(sp.salesPersonId),
        getSpMotivation(sp.salesPersonId).catch(() => ({ ok: true as const, goal: null, todayQuote: null, currentWeekKey: '' })),
      ]);
      setData(dash);
      setGoal(motiv.goal);
      setTodayQ(motiv.todayQuote);
    }
    catch (e: any) { toast.error(e.message); }
    finally { setLoad(false); }
  }, [sp.salesPersonId]);

  useEffect(() => { load(); }, [load]);

  // Auto-reload when the tab becomes visible again (e.g. SP returns after admin marks incentives)
  useEffect(() => {
    const onVisible = () => { if (document.visibilityState === 'visible') load(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [load]);

  /* Loading */
  if (loading) return (
    <div style={{ minHeight:'100vh', display:'flex', flexDirection:'column', alignItems:'center',
      justifyContent:'center', background:'linear-gradient(135deg,#667eea,#764ba2)' }}>
      <style>{CSS}</style>
      <div className="animate-spin" style={{ width:50, height:50, borderRadius:'50%',
        border:'4px solid rgba(255,255,255,.3)', borderTopColor:'#fff' }} />
      <p style={{ marginTop:14, color:'rgba(255,255,255,.85)', fontSize:14, fontWeight:600 }}>
        Loading your dashboard…
      </p>
    </div>
  );
  if (!data) return null;

  const {
    currentWeek:cw, weeks, allTimeSales, allTimeEarned,
    allTimePending, allTimeRunaways, allTimeGiven, allTimeOutstanding,
  } = data;
  const ti       = getTierInfo(cw.tier.name);
  // Use per-SP thresholds from server if available, otherwise fall back to ALL_TIERS
  const thresh   = cw.tier.thresholds;
  const progTo   = thresh && cw.tier.next
    ? (thresh as Record<string, number>)[cw.tier.next.toLowerCase()] || 18
    : ALL_TIERS.find(t=>t.name===cw.tier.next)?.sales || 18;
  const progPct  = cw.tier.next ? Math.min(100, Math.round(cw.salesCount/progTo*100)) : 100;
  const sparkVals= weeks.slice(0,8).map(w=>w.incentive).reverse();
  const wowLast  = weeks[1];
  const wowPct   = wowLast && wowLast.incentive>0
    ? Math.round(((cw.incentive-wowLast.incentive)/wowLast.incentive)*100) : 0;

  const monthKeys  = Array.from(new Set(weeks.map(w=>w.monthKey))).sort((a,b)=>b.localeCompare(a));
  const shownWeeks = mf==='all' ? weeks : weeks.filter(w=>w.monthKey===mf);

  /* Per-week given/ungiven for Overview chart */
  const givenAllTime    = weeks.flatMap(w=>w.payments).filter(p=>p.isFullyPaid&&p.incentivePaid).reduce((s,p)=>s+p.incentive,0);
  const notGivenAllTime = weeks.flatMap(w=>w.payments).filter(p=>p.isFullyPaid&&!p.incentivePaid).reduce((s,p)=>s+p.incentive,0);

  const NAV = [
    { id:'overview' as Tab, label:'Overview',   emoji:'🏠' },
    { id:'weekly'   as Tab, label:'Weekly',     emoji:'📅' },
    { id:'tiers'    as Tab, label:'Tiers',      emoji:'🏆' },
    { id:'slots'    as Tab, label:'Slots',      emoji:'🕐' },
    { id:'eod'      as Tab, label:'EOD Report', emoji:'📋' },
    { id:'leave'    as Tab, label:'Leave',      emoji:'🌴' },
  ];

  return (
    <div style={{ minHeight:'100vh', background:'#f0f4ff', fontFamily:'"Plus Jakarta Sans",Inter,system-ui,sans-serif', display:'flex' }}>
      <style>{CSS}</style>

      {/* ══ SIDEBAR OVERLAY (mobile) ══ */}
      {sideOpen && (
        <div className="sidebar-overlay fixed inset-0 z-30 bg-black/40"
          onClick={() => setSide(false)} />
      )}

      {/* ══ LEFT SIDEBAR ══ */}
      <aside className={`sidebar ${sideOpen ? 'open' : ''}`} style={{
        width:220, flexShrink:0, background:'#fff',
        borderRight:'1px solid #e8ecf6', position:'fixed', top:0, left:0, bottom:0, zIndex:40,
        display:'flex', flexDirection:'column', overflowY:'auto',
        boxShadow:'2px 0 16px rgba(0,0,0,.06)',
      }}>
        {/* Brand / SP identity */}
        <div style={{ background: ti.grad, padding:'20px 16px 18px', flexShrink:0, position:'relative' }}>
          <div style={{ display:'flex', justifyContent:'flex-end', marginBottom:10 }}>
            <button className="hamburger" onClick={() => setSide(false)} style={{
              background:'rgba(255,255,255,.22)', border:'none', borderRadius:8,
              width:28, height:28, cursor:'pointer', color:'#fff', fontSize:16, fontWeight:700,
              display:'flex', alignItems:'center', justifyContent:'center',
            }}>✕</button>
          </div>
          {/* Avatar */}
          <div style={{ width:52, height:52, borderRadius:'50%', background:'rgba(255,255,255,.28)',
            border:'2.5px solid rgba(255,255,255,.6)', display:'flex', alignItems:'center',
            justifyContent:'center', fontSize:22, fontWeight:900, color:'#fff', marginBottom:10,
            boxShadow:'0 4px 16px rgba(0,0,0,.2)' }}>
            {sp.name.charAt(0).toUpperCase()}
          </div>
          <p style={{ fontSize:10, color:'rgba(255,255,255,.7)', fontWeight:700, marginBottom:2 }}>Hey there 👋</p>
          <p style={{ fontSize:16, fontWeight:900, color:'#fff', letterSpacing:'-.01em', lineHeight:1.2 }}>{sp.name}</p>
          <p style={{ fontSize:10, color:'rgba(255,255,255,.65)', marginTop:2, fontWeight:600 }}>{sp.salesPersonId}</p>
          {/* Tier badge */}
          <div style={{ marginTop:10, display:'inline-flex', alignItems:'center', gap:5,
            background:'rgba(255,255,255,.22)', color:'#fff',
            fontSize:11, fontWeight:800, padding:'5px 12px', borderRadius:99,
            border:'1px solid rgba(255,255,255,.3)' }}>
            {ti.emoji} {cw.tier.name} · {cw.tier.pct}%
          </div>
        </div>

        {/* Tier progress */}
        {cw.tier.next && (
          <div style={{ padding:'12px 16px', borderBottom:'1px solid #f1f5f9' }}>
            <div style={{ display:'flex', justifyContent:'space-between', marginBottom:5 }}>
              <p style={{ fontSize:10, color:'#94a3b8', fontWeight:700 }}>To {cw.tier.next}</p>
              <p style={{ fontSize:10, fontWeight:800, color: ti.accent }}>{cw.salesCount}/{progTo}</p>
            </div>
            <div style={{ height:6, borderRadius:99, background:'#f1f5f9', overflow:'hidden' }}>
              <div style={{ height:'100%', width:`${progPct}%`, borderRadius:99, background: ti.grad,
                transition:'width 1s ease' }} />
            </div>
            <p style={{ fontSize:10, color:'#94a3b8', marginTop:4 }}>
              {cw.tier.needed} more to unlock {cw.tier.next}
            </p>
          </div>
        )}

        {/* Nav items */}
        <nav style={{ padding:'10px 10px', flex:1 }}>
          <button onClick={openCrm} style={{
            width:'100%', display:'flex', alignItems:'center', gap:10, padding:'11px 14px', borderRadius:12,
            border:'none', cursor:'pointer', marginBottom:12, fontFamily:'inherit', fontSize:13, fontWeight:800,
            color:'#fff', background:'linear-gradient(135deg,#2b2240,#5b21b6)', boxShadow:'0 6px 18px rgba(91,33,182,.3)',
          }}>
            <span style={{ fontSize:16 }}>📞</span> Open Sales CRM <span style={{ marginLeft:'auto', opacity:.7 }}>↗</span>
          </button>
          <p style={{ fontSize:10, fontWeight:800, color:'#cbd5e1', textTransform:'uppercase',
            letterSpacing:'.08em', padding:'4px 6px 8px' }}>Navigation</p>
          {NAV.map(n => (
            <button key={n.id} className={`nav-item ${tab===n.id ? 'active' : ''}`}
              onClick={() => { setTab(n.id); setSide(false); }}>
              <span style={{ fontSize:16 }}>{n.emoji}</span> {n.label}
            </button>
          ))}
        </nav>

        {/* Incentive colour legend */}
        <div style={{ padding:'12px 14px', borderTop:'1px solid #f1f5f9', margin:'0 8px 8px' }}>
          <p style={{ fontSize:10, fontWeight:800, color:'#94a3b8', textTransform:'uppercase',
            letterSpacing:'.07em', marginBottom:8 }}>Incentive Status</p>
          {Object.entries(INC).map(([k,v]) => (
            <div key={k} style={{ display:'flex', alignItems:'center', gap:7, marginBottom:6 }}>
              <span style={{ width:10, height:10, borderRadius:3, background:v.bg,
                border:`1.5px solid ${v.border}`, flexShrink:0 }} />
              <p style={{ fontSize:10, fontWeight:600, color:v.text }}>{v.label}</p>
            </div>
          ))}
        </div>

        {/* Sign out */}
        <div style={{ padding:'12px', borderTop:'1px solid #f1f5f9', flexShrink:0 }}>
          <button onClick={() => { logout(); navigate('/tracker/sales/login'); }}
            style={{ width:'100%', display:'flex', alignItems:'center', gap:8,
              padding:'10px 12px', borderRadius:10, border:'none',
              background:'#fef2f2', color:'#dc2626', cursor:'pointer',
              fontSize:12, fontWeight:700, fontFamily:'inherit' }}>
            🚪 Sign Out
          </button>
        </div>
      </aside>

      {/* ══ MAIN CONTENT ══ */}
      <div className="main-wrap" style={{ marginLeft:220, flex:1, minWidth:0, display:'flex', flexDirection:'column' }}>

        {/* ── TOP HEADER ── */}
        <header style={{
          position:'sticky', top:0, zIndex:20,
          background:'#fff', borderBottom:'1px solid #e8ecf6',
          padding:'12px 20px', display:'flex', alignItems:'center',
          justifyContent:'space-between', gap:12,
          boxShadow:'0 1px 8px rgba(0,0,0,.05)',
        }}>
          <div style={{ display:'flex', alignItems:'center', gap:12 }}>
            {/* Hamburger (mobile only) */}
            <button className="hamburger" onClick={() => setSide(true)} style={{
              width:34, height:34, borderRadius:10, border:'1.5px solid #e2e8f0',
              background:'#fff', cursor:'pointer', display:'flex',
              alignItems:'center', justifyContent:'center', fontSize:18, color:'#64748b',
            }}>☰</button>
            <div>
              <p style={{ fontSize:16, fontWeight:900, color:'#0f172a', letterSpacing:'-.02em' }}>
                {{overview:'🏠 Dashboard', weekly:'📅 Weekly Report', tiers:'🏆 Tier Overview', slots:'🕐 Available Slots', eod:'📋 EOD Report', leave:'🌴 My Leave'}[tab]}
              </p>
              <p style={{ fontSize:11, color:'#94a3b8', marginTop:1 }}>
                {cw.weekRange} · {ti.emoji} {cw.tier.name}
              </p>
            </div>
          </div>
          <div style={{ display:'flex', alignItems:'center', gap:8 }}>
            {/* WoW chip */}
            {weeks.length > 1 && (
              <span style={{
                display:'inline-flex', alignItems:'center', gap:4,
                background: wowPct >= 0 ? '#dcfce7' : '#fee2e2',
                color:       wowPct >= 0 ? '#15803d' : '#dc2626',
                fontSize:11, fontWeight:800, padding:'4px 10px', borderRadius:99,
              }}>
                {wowPct >= 0 ? '📈' : '📉'} {wowPct >= 0 ? '+' : ''}{wowPct}% vs last week
              </span>
            )}
            <button onClick={load} title="Refresh" style={{
              width:34, height:34, borderRadius:10, border:'1.5px solid #e2e8f0',
              background:'#fff', cursor:'pointer', display:'flex',
              alignItems:'center', justifyContent:'center', fontSize:16,
            }}>🔄</button>
          </div>
        </header>

        {/* ── PAGE BODY ── */}
        <div style={{ padding:'18px 20px 80px', flex:1 }}>

          {/* ══════════ OVERVIEW TAB ══════════ */}
          {tab === 'overview' && (
            <>
              {/* ── DAILY QUOTE ── */}
              <DailyQuoteCard todayQ={todayQ} />

              {/* ── MOTIVATION HERO ── */}
              {goal && (
                <MotivationHero
                  goal={goal}
                  spName={sp.name}
                  salesCount={cw.salesCount}
                />
              )}

              {/* ── KPI CARDS (4 cards) ── */}
              <div className="fu1" style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(140px,1fr))', gap:12, marginBottom:20 }}>
                {/* 1 — Given */}
                <div className="kpi" style={{
                  borderRadius:18, padding:'16px',
                  background:'linear-gradient(135deg,#16a34a,#4ade80)',
                  boxShadow:'0 8px 24px rgba(22,163,74,.35)', color:'#fff',
                }}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:10 }}>
                    <div style={{ width:36, height:36, borderRadius:12, background:'rgba(255,255,255,.22)',
                      display:'flex', alignItems:'center', justifyContent:'center', fontSize:18 }}>
                      ✅
                    </div>
                    <span style={{ fontSize:9, fontWeight:800, background:'rgba(255,255,255,.25)',
                      padding:'3px 8px', borderRadius:99, textTransform:'uppercase', letterSpacing:'.05em' }}>
                      All-time
                    </span>
                  </div>
                  <p style={{ fontSize:26, fontWeight:900, letterSpacing:'-.02em', lineHeight:1 }}>
                    <Counter to={allTimeGiven} prefix="₹" />
                  </p>
                  <p style={{ fontSize:11, opacity:.85, fontWeight:600, marginTop:5 }}>Incentive Given</p>
                  <p style={{ fontSize:9, opacity:.7, marginTop:2 }}>Admin confirmed</p>
                </div>

                {/* 2 — Outstanding (earned, not given) */}
                <div className="kpi" style={{
                  borderRadius:18, padding:'16px',
                  background:'linear-gradient(135deg,#d97706,#fbbf24)',
                  boxShadow:'0 8px 24px rgba(217,119,6,.35)', color:'#fff',
                }}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:10 }}>
                    <div style={{ width:36, height:36, borderRadius:12, background:'rgba(255,255,255,.22)',
                      display:'flex', alignItems:'center', justifyContent:'center', fontSize:18 }}>
                      💰
                    </div>
                    <span style={{ fontSize:9, fontWeight:800, background:'rgba(255,255,255,.25)',
                      padding:'3px 8px', borderRadius:99, textTransform:'uppercase', letterSpacing:'.05em' }}>
                      Pending
                    </span>
                  </div>
                  <p style={{ fontSize:26, fontWeight:900, letterSpacing:'-.02em', lineHeight:1 }}>
                    <Counter to={allTimeOutstanding} prefix="₹" />
                  </p>
                  <p style={{ fontSize:11, opacity:.85, fontWeight:600, marginTop:5 }}>Awaiting Payment</p>
                  <p style={{ fontSize:9, opacity:.7, marginTop:2 }}>Earned, not given yet</p>
                </div>

                {/* 3 — This week earned */}
                <div className="kpi" style={{
                  borderRadius:18, padding:'16px',
                  background:'linear-gradient(135deg,#667eea,#764ba2)',
                  boxShadow:'0 8px 24px rgba(118,75,162,.35)', color:'#fff',
                }}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:10 }}>
                    <div style={{ width:36, height:36, borderRadius:12, background:'rgba(255,255,255,.22)',
                      display:'flex', alignItems:'center', justifyContent:'center', fontSize:18 }}>
                      💳
                    </div>
                    <span style={{ fontSize:9, fontWeight:800, background:'rgba(255,255,255,.25)',
                      padding:'3px 8px', borderRadius:99, textTransform:'uppercase', letterSpacing:'.05em' }}>
                      This week
                    </span>
                  </div>
                  <p style={{ fontSize:26, fontWeight:900, letterSpacing:'-.02em', lineHeight:1 }}>
                    <Counter to={cw.incentive} prefix="₹" />
                  </p>
                  <p style={{ fontSize:11, opacity:.85, fontWeight:600, marginTop:5 }}>Earned This Week</p>
                  {cw.pendingIncentive > 0 && (
                    <p style={{ fontSize:9, opacity:.7, marginTop:2 }}>+{fmtINR(cw.pendingIncentive)} pending</p>
                  )}
                </div>

                {/* 4 — Students */}
                <div className="kpi" style={{
                  borderRadius:18, padding:'16px',
                  background:'linear-gradient(135deg,#0ea5e9,#38bdf8)',
                  boxShadow:'0 8px 24px rgba(14,165,233,.35)', color:'#fff',
                }}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:10 }}>
                    <div style={{ width:36, height:36, borderRadius:12, background:'rgba(255,255,255,.22)',
                      display:'flex', alignItems:'center', justifyContent:'center', fontSize:18 }}>
                      👥
                    </div>
                    <span style={{ fontSize:9, fontWeight:800, background:'rgba(255,255,255,.25)',
                      padding:'3px 8px', borderRadius:99, textTransform:'uppercase', letterSpacing:'.05em' }}>
                      All-time
                    </span>
                  </div>
                  <p style={{ fontSize:26, fontWeight:900, letterSpacing:'-.02em', lineHeight:1 }}>
                    <Counter to={allTimeSales} />
                  </p>
                  <p style={{ fontSize:11, opacity:.85, fontWeight:600, marginTop:5 }}>Students Enrolled</p>
                  <p style={{ fontSize:9, opacity:.7, marginTop:2 }}>{allTimeRunaways} ran away</p>
                </div>
              </div>

              {/* ── Sales Overview Chart ── */}
              <div className="fu2" style={{
                background:'#fff', borderRadius:20, padding:'18px 16px 14px',
                boxShadow:'0 2px 12px rgba(0,0,0,.07)', marginBottom:16,
              }}>
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:14 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                    <span style={{ fontSize:16 }}>📊</span>
                    <p style={{ fontSize:14, fontWeight:800, color:'#0f172a' }}>Sales Overview</p>
                  </div>
                  <p style={{ fontSize:11, color:'#94a3b8' }}>Last 8 weeks</p>
                </div>
                <OverviewChart weeks={weeks} onBarClick={setDrawer} />
              </div>

              {/* ── Incentive Status Summary ── */}
              <div className="fu3" style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:16 }}>
                {/* Given vs Outstanding donut */}
                <div style={{ background:'#fff', borderRadius:20, padding:'16px',
                  boxShadow:'0 2px 10px rgba(0,0,0,.07)' }}>
                  <p style={{ fontSize:11, fontWeight:800, color:'#64748b', textTransform:'uppercase',
                    letterSpacing:'.06em', marginBottom:10 }}>Incentive Status</p>
                  <div style={{ display:'flex', justifyContent:'center' }}>
                    <Donut size={110} thick={13}
                      center={fmtINR(allTimeEarned).replace('₹','')}
                      csub="total"
                      segs={[
                        { val: givenAllTime,    color:'#4ade80' },
                        { val: notGivenAllTime, color:'#fcd34d' },
                      ]} />
                  </div>
                  <div style={{ display:'flex', flexDirection:'column', gap:5, marginTop:8 }}>
                    {[
                      { color:'#4ade80', label:'Given',    val: fmtINR(givenAllTime) },
                      { color:'#fcd34d', label:'Awaiting', val: fmtINR(notGivenAllTime) },
                    ].map(x => (
                      <div key={x.label} style={{ display:'flex', alignItems:'center', gap:7 }}>
                        <span style={{ width:8, height:8, borderRadius:'50%', background:x.color }} />
                        <p style={{ fontSize:11, color:'#64748b', flex:1 }}>{x.label}</p>
                        <p style={{ fontSize:11, fontWeight:800, color:'#0f172a' }}>{x.val}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* This week breakdown donut */}
                <div style={{ background:'#fff', borderRadius:20, padding:'16px',
                  boxShadow:'0 2px 10px rgba(0,0,0,.07)' }}>
                  <p style={{ fontSize:11, fontWeight:800, color:'#64748b', textTransform:'uppercase',
                    letterSpacing:'.06em', marginBottom:10 }}>This Week</p>
                  <div style={{ display:'flex', justifyContent:'center' }}>
                    <Donut size={110} thick={13}
                      center={String(cw.payments.length)}
                      csub="students"
                      segs={[
                        { val: cw.payments.filter(p=>p.isFullyPaid&&p.incentivePaid).length,  color:'#4ade80' },
                        { val: cw.payments.filter(p=>p.isFullyPaid&&!p.incentivePaid).length, color:'#fcd34d' },
                        { val: cw.pendingCount,  color:'#7dd3fc' },
                        { val: cw.runaways,      color:'#f87171' },
                      ]} />
                  </div>
                  <div style={{ display:'flex', flexDirection:'column', gap:4, marginTop:8 }}>
                    {[
                      { color:'#4ade80', label:'Given',   val:cw.payments.filter(p=>p.isFullyPaid&&p.incentivePaid).length },
                      { color:'#fcd34d', label:'Awaiting',val:cw.payments.filter(p=>p.isFullyPaid&&!p.incentivePaid).length },
                      { color:'#7dd3fc', label:'Partial', val:cw.pendingCount },
                      { color:'#f87171', label:'Ran Away',val:cw.runaways },
                    ].filter(x=>x.val>0).map(x => (
                      <div key={x.label} style={{ display:'flex', alignItems:'center', gap:7 }}>
                        <span style={{ width:8, height:8, borderRadius:'50%', background:x.color }} />
                        <p style={{ fontSize:11, color:'#64748b', flex:1 }}>{x.label}</p>
                        <p style={{ fontSize:12, fontWeight:900, color:'#0f172a' }}>{x.val}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* ── This week student list ── */}
              {cw.payments.length > 0 && (
                <div className="fu4" style={{ background:'#fff', borderRadius:20, overflow:'hidden',
                  boxShadow:'0 2px 12px rgba(0,0,0,.08)' }}>
                  <div style={{ padding:'14px 16px 8px', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                    <p style={{ fontSize:14, fontWeight:800, color:'#0f172a' }}>This Week's Students</p>
                    <button onClick={() => setDrawer(cw)} style={{
                      fontSize:12, fontWeight:700, color:'#6d28d9', background:'#ede9fe',
                      border:'none', padding:'5px 12px', borderRadius:99, cursor:'pointer',
                    }}>
                      View All →
                    </button>
                  </div>
                  {cw.payments.slice(0,5).map((p,i) => (
                    <PayRow key={p.id} p={p} rank={i+1} pct={cw.tier.pct} last={i===Math.min(4,cw.payments.length-1)} />
                  ))}
                  {cw.payments.length > 5 && (
                    <button onClick={() => setDrawer(cw)} style={{
                      width:'100%', padding:'11px', border:'none', cursor:'pointer',
                      background:'#faf5ff', color:'#7c3aed', fontSize:12, fontWeight:700,
                      display:'flex', alignItems:'center', justifyContent:'center', gap:5,
                    }}>
                      +{cw.payments.length-5} more →
                    </button>
                  )}
                </div>
              )}
              {cw.payments.length === 0 && (
                <div className="fu4" style={{ background:'#fff', borderRadius:20, padding:'40px 20px',
                  textAlign:'center', boxShadow:'0 2px 8px rgba(0,0,0,.06)' }}>
                  <div style={{ fontSize:40 }}>🎯</div>
                  <p style={{ fontWeight:800, color:'#1e293b', marginTop:8 }}>No students yet this week</p>
                  <p style={{ fontSize:13, color:'#94a3b8', marginTop:4 }}>Enroll students to start earning</p>
                </div>
              )}
            </>
          )}

          {/* ══════════ WEEKLY TAB ══════════ */}
          {tab === 'weekly' && (
            <>
              {/* Month filter */}
              {monthKeys.length > 1 && (
                <div className="fu1" style={{ display:'flex', gap:6, overflowX:'auto', paddingBottom:4, marginBottom:14 }}>
                  {[['all','All Months'], ...monthKeys.map(mk => {
                    const [y,m] = mk.split('-');
                    return [mk, new Date(+y,+m-1).toLocaleDateString('en-IN',{month:'short',year:'2-digit'})];
                  })].map(([id,label]) => (
                    <button key={id} onClick={() => setMf(id as string)} style={{
                      flexShrink:0, padding:'6px 14px', borderRadius:99,
                      fontSize:12, fontWeight:700, cursor:'pointer',
                      border: mf===id ? 'none' : '1.5px solid #e2e8f0',
                      background: mf===id ? ti.badge : '#fff',
                      color:       mf===id ? '#fff' : '#64748b',
                      boxShadow:   mf===id ? `0 4px 12px ${ti.badge}55` : 'none',
                      transition:'all .15s ease',
                    }}>
                      {label}
                    </button>
                  ))}
                </div>
              )}

              {/* Period KPIs */}
              {shownWeeks.length > 0 && (
                <div className="fu2" style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:10, marginBottom:14 }}>
                  {[
                    { label:'Earned',  val:fmtINR(shownWeeks.reduce((s,w)=>s+w.incentive,0)), grad:'linear-gradient(135deg,#d1fae5,#a7f3d0)', color:'#065f46' },
                    { label:'Given',   val:fmtINR(shownWeeks.flatMap(w=>w.payments).filter(p=>p.isFullyPaid&&p.incentivePaid).reduce((s,p)=>s+p.incentive,0)), grad:'linear-gradient(135deg,#dcfce7,#bbf7d0)', color:'#15803d' },
                    { label:'Pending', val:fmtINR(shownWeeks.reduce((s,w)=>s+w.pendingIncentive,0)), grad:'linear-gradient(135deg,#fef9c3,#fde68a)', color:'#78350f' },
                  ].map(s => (
                    <div key={s.label} style={{ background:s.grad, borderRadius:14, padding:'12px',
                      boxShadow:'0 1px 4px rgba(0,0,0,.06)' }}>
                      <p style={{ fontSize:16, fontWeight:900, color:s.color }}>{s.val}</p>
                      <p style={{ fontSize:10, color:s.color, opacity:.7, fontWeight:700,
                        textTransform:'uppercase', letterSpacing:'.05em', marginTop:3 }}>{s.label}</p>
                    </div>
                  ))}
                </div>
              )}

              {/* Week list */}
              <div className="fu3" style={{ display:'flex', flexDirection:'column', gap:8 }}>
                {shownWeeks.length === 0 ? (
                  <div style={{ background:'#fff', borderRadius:16, padding:'40px', textAlign:'center' }}>
                    <p style={{ color:'#94a3b8' }}>No data for this period</p>
                  </div>
                ) : shownWeeks.map(w => {
                  const wt         = getTierInfo(w.tier.name);
                  const givenAmt   = w.payments.filter(p=>p.isFullyPaid&&p.incentivePaid).reduce((s,p)=>s+p.incentive,0);
                  const awaitAmt   = w.incentive - givenAmt;
                  const fullyPaid  = w.payments.filter(p=>p.isFullyPaid).length;
                  return (
                    <div key={w.weekKey} className="week-row" onClick={() => setDrawer(w)} style={{
                      background:'#fff', borderRadius:18, overflow:'hidden',
                      boxShadow:'0 2px 8px rgba(0,0,0,.07)',
                    }}>
                      {/* Top colour bar */}
                      <div style={{ height:4, background: wt.grad }} />
                      <div style={{ padding:'13px 14px' }}>
                        <div style={{ display:'flex', alignItems:'flex-start', gap:10, justifyContent:'space-between' }}>
                          <div style={{ flex:1, minWidth:0 }}>
                            <div style={{ display:'flex', alignItems:'center', gap:7, marginBottom:3 }}>
                              <p style={{ fontSize:14, fontWeight:800, color:'#0f172a' }}>{w.weekLabel}</p>
                              {w.isCurrentWeek && (
                                <span style={{ background:wt.badge, color:'#fff', fontSize:9, fontWeight:800,
                                  padding:'2px 7px', borderRadius:99, textTransform:'uppercase', letterSpacing:'.05em' }}>
                                  NOW
                                </span>
                              )}
                            </div>
                            <p style={{ fontSize:11, color:'#94a3b8', marginBottom:8 }}>
                              {w.weekRange} · {w.salesCount} enrolled · {fullyPaid} fully paid
                            </p>
                            {/* Incentive breakdown */}
                            <div style={{ display:'flex', gap:5, flexWrap:'wrap' }}>
                              {givenAmt > 0 && (
                                <span style={{ background:'#dcfce7', color:'#15803d', border:'1px solid #86efac',
                                  fontSize:10, fontWeight:800, padding:'2px 8px', borderRadius:99 }}>
                                  ✓ {fmtINR(givenAmt)} given
                                </span>
                              )}
                              {awaitAmt > 0 && (
                                <span style={{ background:'#fef9c3', color:'#92400e', border:'1px solid #fcd34d',
                                  fontSize:10, fontWeight:800, padding:'2px 8px', borderRadius:99 }}>
                                  ⏳ {fmtINR(awaitAmt)} awaiting
                                </span>
                              )}
                              {w.pendingIncentive > 0 && (
                                <span style={{ background:'#e0f2fe', color:'#075985', border:'1px solid #7dd3fc',
                                  fontSize:10, fontWeight:700, padding:'2px 8px', borderRadius:99 }}>
                                  + {fmtINR(w.pendingIncentive)} if paid
                                </span>
                              )}
                            </div>
                          </div>
                          <div style={{ textAlign:'right', flexShrink:0 }}>
                            <p style={{ fontSize:18, fontWeight:900, letterSpacing:'-.02em',
                              color: w.incentive>0 ? '#0f172a' : '#cbd5e1' }}>
                              {fmtINR(w.incentive)}
                            </p>
                            <span style={{ background:wt.light, color:wt.accent, fontSize:10, fontWeight:800,
                              padding:'2px 8px', borderRadius:99, marginTop:4, display:'inline-block' }}>
                              {w.tier.name} {w.tier.pct}%
                            </span>
                            <div style={{ marginTop:4, fontSize:14, color:'#cbd5e1' }}>→</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {/* ══════════ TIERS TAB ══════════ */}
          {tab === 'tiers' && (
            <>
              {/* Current tier card */}
              <div className="fu1" style={{ borderRadius:24, padding:'22px', marginBottom:16,
                background:ti.grad, boxShadow:`0 12px 32px ${ti.badge}55`, color:'#fff' }}>
                <div style={{ display:'flex', alignItems:'center', gap:16 }}>
                  <Ring pct={progPct} size={90} thick={9} color="rgba(255,255,255,.95)"
                    label={cw.tier.next ? String(cw.salesCount) : '✓'}
                    sub={cw.tier.next ? `of ${progTo}` : 'MAX'} />
                  <div style={{ flex:1 }}>
                    <p style={{ fontSize:11, opacity:.75, textTransform:'uppercase', letterSpacing:'.08em', fontWeight:700 }}>
                      Current Tier
                    </p>
                    <p style={{ fontSize:28, fontWeight:900, letterSpacing:'-.02em', marginTop:4, lineHeight:1 }}>
                      {cw.tier.name}
                    </p>
                    <p style={{ fontSize:38, fontWeight:900, letterSpacing:'-.02em', lineHeight:1 }}>
                      {cw.tier.pct}%
                    </p>
                    {cw.tier.next && (
                      <div style={{ marginTop:10, display:'inline-flex', alignItems:'center', gap:5,
                        background:'rgba(255,255,255,.22)', padding:'5px 10px', borderRadius:10 }}>
                            <span style={{ fontSize:11 }}>🎯</span>
                        <p style={{ fontSize:11, color:'#fff', fontWeight:700 }}>
                          {cw.tier.needed} more → {cw.tier.next}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
                {/* Sparkline strip */}
                {sparkVals.length >= 2 && (
                  <div style={{ marginTop:16, opacity:.8 }}>
                    <p style={{ fontSize:10, opacity:.7, fontWeight:700, letterSpacing:'.06em',
                      textTransform:'uppercase', marginBottom:4 }}>
                      Incentive Trend (last {sparkVals.length} weeks)
                    </p>
                    <Sparkline vals={sparkVals} color="rgba(255,255,255,.95)" w={260} h={40} />
                  </div>
                )}
              </div>

              {/* Best week */}
              {weeks.length > 0 && (() => {
                const best = [...weeks].sort((a,b)=>b.incentive-a.incentive)[0];
                const bt   = getTierInfo(best.tier.name);
                return (
                  <div className="fu2" onClick={() => setDrawer(best)} style={{
                    background:'#fff', borderRadius:18, padding:'16px',
                    boxShadow:'0 2px 10px rgba(0,0,0,.08)', marginBottom:14,
                    display:'flex', alignItems:'center', gap:14, cursor:'pointer',
                  }}>
                    <div style={{ width:48, height:48, borderRadius:14, background:bt.grad, flexShrink:0,
                      display:'flex', alignItems:'center', justifyContent:'center', fontSize:22 }}>
                      🏆
                    </div>
                    <div style={{ flex:1 }}>
                      <p style={{ fontSize:10, fontWeight:800, color:'#94a3b8', textTransform:'uppercase', letterSpacing:'.07em' }}>
                        Best Week 🏆
                      </p>
                      <p style={{ fontSize:22, fontWeight:900, color:'#0f172a', letterSpacing:'-.02em' }}>
                        {fmtINR(best.incentive)}
                      </p>
                      <p style={{ fontSize:11, color:'#64748b', marginTop:2 }}>
                        {best.weekLabel} · {best.tier.name} {best.tier.pct}% · {best.salesCount} enrolled
                      </p>
                    </div>
                    <span style={{ fontSize:16, color:'#cbd5e1' }}>→</span>
                  </div>
                );
              })()}

              {/* Tier ladder */}
              <div className="fu3" style={{ background:'#fff', borderRadius:20, overflow:'hidden',
                boxShadow:'0 2px 10px rgba(0,0,0,.07)', marginBottom:14 }}>
                <div style={{ padding:'14px 16px 8px', display:'flex', alignItems:'center', gap:6 }}>
                  <span style={{ fontSize:14 }}>🔥</span>
                  <p style={{ fontSize:12, fontWeight:800, color:'#64748b', textTransform:'uppercase', letterSpacing:'.07em' }}>
                    Incentive Ladder
                  </p>
                </div>
                {[...ALL_TIERS].reverse().map((t, idx) => {
                  const active  = t.name === cw.tier.name;
                  const reached = ALL_TIERS.findIndex(x=>x.name===t.name) <= ALL_TIERS.findIndex(x=>x.name===cw.tier.name);
                  const tI      = getTierInfo(t.name);
                  return (
                    <div key={t.name} style={{
                      display:'flex', alignItems:'center', gap:12, padding:'12px 16px',
                      background: active ? tI.light : 'transparent',
                      borderBottom: idx<ALL_TIERS.length-1 ? '1px solid #f8fafc' : 'none',
                    }}>
                      <div style={{ width:40, height:40, borderRadius:12,
                        background: reached ? tI.grad : '#f1f5f9', flexShrink:0,
                        display:'flex', alignItems:'center', justifyContent:'center',
                        transition:'background .3s', fontSize:20,
                        opacity: reached ? 1 : 0.35 }}>
                        {tI.emoji}
                      </div>
                      <div style={{ flex:1 }}>
                        <div style={{ display:'flex', alignItems:'center', gap:7 }}>
                          <p style={{ fontSize:14, fontWeight:800, color: active ? tI.accent : '#0f172a' }}>{t.name}</p>
                          {active && <span style={{ background:tI.badge, color:'#fff', fontSize:9, fontWeight:800,
                            padding:'2px 7px', borderRadius:99 }}>You're here</span>}
                          {reached && !active && <span style={{ color:'#22c55e', fontSize:12 }}>✓</span>}
                        </div>
                        <p style={{ fontSize:11, color:'#94a3b8', marginTop:1 }}>{t.sales} sales / week</p>
                      </div>
                      <p style={{ fontSize:26, fontWeight:900, letterSpacing:'-.02em',
                        color: reached ? tI.accent : '#e2e8f0', transition:'color .3s' }}>
                        {t.pct}%
                      </p>
                    </div>
                  );
                })}
              </div>

              {/* All-time */}
              <div className="fu4" style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
                {[
                  { label:'Given',    val:allTimeGiven,       grad:'linear-gradient(135deg,#d1fae5,#a7f3d0)', color:'#065f46', currency:true },
                  { label:'Awaiting', val:allTimeOutstanding, grad:'linear-gradient(135deg,#fef9c3,#fde68a)', color:'#78350f', currency:true },
                  { label:'Enrolled', val:allTimeSales,       grad:'linear-gradient(135deg,#ede9fe,#ddd6fe)', color:'#4c1d95' },
                  { label:'Runaways', val:allTimeRunaways,    grad:'linear-gradient(135deg,#fee2e2,#fecaca)', color:'#991b1b' },
                ].map(s => (
                  <div key={s.label} style={{ background:s.grad, borderRadius:16, padding:'14px', boxShadow:'0 1px 4px rgba(0,0,0,.06)' }}>
                    <p style={{ fontSize:22, fontWeight:900, color:s.color, letterSpacing:'-.02em' }}>
                      <Counter to={s.val} prefix={s.currency ? '₹' : ''} />
                    </p>
                    <p style={{ fontSize:10, color:s.color, opacity:.75, fontWeight:700,
                      textTransform:'uppercase', letterSpacing:'.05em', marginTop:4 }}>
                      {s.label}
                    </p>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* ══════════ EOD TAB ══════════ */}
          {tab === 'slots' && <SlotsTab />}
          {tab === 'eod' && <EodTab salesPersonId={sp.salesPersonId} />}
          {tab === 'leave' && (
            <div className="fu1" style={{ paddingBottom: 48 }}>
              <div style={{ textAlign: 'center', marginBottom: 20 }}>
                <div style={{ fontSize: 40, marginBottom: 6 }}>🌴</div>
                <h2 style={{ fontSize: 22, fontWeight: 900, color: '#0f172a', margin: 0 }}>My Leave</h2>
                <p style={{ fontSize: 12, color: '#94a3b8', marginTop: 4 }}>Apply for leave — admin approves it, and approved leave shows on your payslip.</p>
              </div>
              <LeavePanel employeeType="sales" employeeId={sp.salesPersonId} />
            </div>
          )}

        </div>
      </div>

      {/* Drawer */}
      {drawer && <Drawer week={drawer} onClose={() => setDrawer(null)} />}
    </div>
  );
};

export default SalesDashboard;
